import React from "react";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { Box, Typography, Paper, Grid, Card, Divider } from "@mui/material";
import {
  AccountBalance,
  Person,
  Business,
  TrendingUp,
  Payment,
  CheckCircle,
} from "@mui/icons-material";
import "./OrganisationPresentation.css";

const OrganisationPresentation = () => {
  return (
    <>
      <Helmet>
        <title>Organisations - Mission Sport</title>
        <meta
          name="description"
          content="Trouvez les meilleurs professionnels du sport pour vos missions. Découvrez comment Mission Sport peut aider votre organisation à recruter des talents qualifiés."
        />
      </Helmet>

      <div className="organisation-presentation">
        {/* Hero Section */}
        <section className="hero-section">
          <div className="hero-container">
            <div className="hero-content">
              <p className="hero-subtitle">
                Connectez-vous avec des experts qualifiés pour vos missions
                sportives. Simplifiez votre recrutement avec Mission Sport.
              </p>
              <div className="hero-actions">
                <Link to="/register" className="btn btn-primary btn-large">
                  Déposer une mission
                </Link>
                <button className="btn btn-secondary btn-large">
                  Voir comment ça marche
                </button>
              </div>
            </div>
            <div className="hero-image">
              <div className="hero-placeholder">
                <span>🏆</span>
                <p>Illustration Hero</p>
              </div>
            </div>
          </div>
        </section>

        {/* Avantages Section */}
        <section className="advantages-section">
          <div className="container">
            <h2 className="section-title">Pourquoi choisir Mission Sport ?</h2>
            <div className="advantages-grid">
              <div className="advantage-card">
                <div className="advantage-icon">🎯</div>
                <h3>Professionnels qualifiés</h3>
                <p>
                  Accédez à un réseau de professionnels du sport vérifiés et
                  expérimentés.
                </p>
              </div>
              <div className="advantage-card">
                <div className="advantage-icon">⚡</div>
                <h3>Recrutement rapide</h3>
                <p>
                  Trouvez le bon profil en quelques clics grâce à nos filtres
                  avancés.
                </p>
              </div>
              <div className="advantage-card">
                <div className="advantage-icon">💰</div>
                <h3>Tarifs transparents</h3>
                <p>
                  Négociez directement avec les professionnels, sans frais
                  cachés.
                </p>
              </div>
              <div className="advantage-card">
                <div className="advantage-icon">🛡️</div>
                <h3>Sécurité garantie</h3>
                <p>
                  Paiements sécurisés et support client dédié pour vos missions.
                </p>
              </div>
              <div className="advantage-card">
                <div className="advantage-icon">📊</div>
                <h3>Suivi en temps réel</h3>
                <p>
                  Tableau de bord complet pour suivre vos missions et
                  candidatures.
                </p>
              </div>
              <div className="advantage-card">
                <div className="advantage-icon">🌟</div>
                <h3>Qualité assurée</h3>
                <p>
                  Système de notation et avis pour garantir la qualité des
                  prestations.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Processus Section */}
        <section className="process-section">
          <div className="container">
            <h2 className="section-title">Comment ça marche ?</h2>
            <div className="process-steps">
              <div className="step">
                <div className="step-number">1</div>
                <div className="step-content">
                  <h3>Créez votre compte</h3>
                  <p>
                    Inscrivez-vous gratuitement et complétez le profil de votre
                    organisation.
                  </p>
                </div>
              </div>
              <div className="step">
                <div className="step-number">2</div>
                <div className="step-content">
                  <h3>Publiez votre mission</h3>
                  <p>
                    Décrivez votre besoin, définissez le budget et les
                    compétences requises.
                  </p>
                </div>
              </div>
              <div className="step">
                <div className="step-number">3</div>
                <div className="step-content">
                  <h3>Recevez des candidatures</h3>
                  <p>
                    Les professionnels intéressés postulent directement à votre
                    mission.
                  </p>
                </div>
              </div>
              <div className="step">
                <div className="step-number">4</div>
                <div className="step-content">
                  <h3>Sélectionnez votre expert</h3>
                  <p>
                    Consultez les profils, échangez et choisissez le meilleur
                    candidat.
                  </p>
                </div>
              </div>
              <div className="step">
                <div className="step-number">5</div>
                <div className="step-content">
                  <h3>Collaborez en toute sérénité</h3>
                  <p>
                    Suivez l'avancement et communiquez via notre plateforme
                    sécurisée.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Témoignages Section */}
        <section className="testimonials-section">
          <div className="container">
            <h2 className="section-title">Ils nous font confiance</h2>
            <div className="testimonials-grid">
              <div className="testimonial-card">
                <div className="testimonial-content">
                  <p>
                    "Mission Sport nous a permis de trouver rapidement un
                    entraîneur qualifié pour notre équipe. Le processus est
                    simple et efficace."
                  </p>
                </div>
                <div className="testimonial-author">
                  <div className="author-avatar">FC</div>
                  <div className="author-info">
                    <h4>Football Club de Paris</h4>
                    <span>Club professionnel</span>
                  </div>
                </div>
                <div className="testimonial-rating">⭐⭐⭐⭐⭐</div>
              </div>
              <div className="testimonial-card">
                <div className="testimonial-content">
                  <p>
                    "Grâce à Mission Sport, nous avons recruté un préparateur
                    physique exceptionnel. La qualité des profils est
                    remarquable."
                  </p>
                </div>
                <div className="testimonial-author">
                  <div className="author-avatar">AS</div>
                  <div className="author-info">
                    <h4>Athletic Salle Lyon</h4>
                    <span>Salle de sport</span>
                  </div>
                </div>
                <div className="testimonial-rating">⭐⭐⭐⭐⭐</div>
              </div>
              <div className="testimonial-card">
                <div className="testimonial-content">
                  <p>
                    "Une plateforme intuitive qui nous fait gagner un temps
                    précieux dans nos recrutements. Je recommande vivement !"
                  </p>
                </div>
                <div className="testimonial-author">
                  <div className="author-avatar">TM</div>
                  <div className="author-info">
                    <h4>Tennis Marseille</h4>
                    <span>Club de tennis</span>
                  </div>
                </div>
                <div className="testimonial-rating">⭐⭐⭐⭐⭐</div>
              </div>
            </div>
          </div>
        </section>

        {/* Pricing Model Section from Pricing.js */}
        <section
          className="pricing-model-section"
          style={{ marginBottom: "80px" }}
        >
          <div className="container">
            {/* Pricing Model Infographic */}
            <Paper elevation={3} sx={{ p: 4, mb: 6, borderRadius: 3 }}>
              <Typography
                variant="h4"
                component="h2"
                sx={{
                  textAlign: "center",
                  fontWeight: 600,
                  color: "primary.main",
                  mb: 4,
                }}
              >
                Comment ça fonctionne ?
              </Typography>

              {/* Flow Diagram */}
              <Box className="pricing-flow" sx={{ position: "relative" }}>
                <Grid container spacing={3} alignItems="center">
                  {/* Step 1: Structure */}
                  <Grid item xs={12} md={3}>
                    <Card
                      className="flow-card"
                      sx={{
                        textAlign: "center",
                        p: 3,
                        height: "100%",
                        bgcolor: "#1976d2",
                      }}
                    >
                      <Business sx={{ fontSize: 48, color: "white", mb: 2 }} />
                      <Typography
                        variant="h6"
                        sx={{ fontWeight: 600, mb: 1, color: "white" }}
                      >
                        1. Structure
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{ mb: 2, color: "white" }}
                      >
                        Prépare la mission
                      </Typography>
                      <Box
                        sx={{ bgcolor: "primary.light", p: 2, borderRadius: 2 }}
                      >
                        <Typography
                          variant="body2"
                          sx={{ fontWeight: 600, color: "white" }}
                        >
                          Taux horaire brut
                        </Typography>
                        <Typography variant="caption" sx={{ color: "white" }}>
                          + Frais de service
                        </Typography>
                      </Box>
                    </Card>
                  </Grid>

                  {/* Arrow 1 */}
                  <Grid item xs={12} md={1} sx={{ textAlign: "center" }}>
                    <Box className="flow-arrow">
                      <TrendingUp
                        sx={{
                          fontSize: 32,
                          color: "primary.main",
                          transform: "rotate(90deg)",
                        }}
                      />
                    </Box>
                  </Grid>

                  {/* Step 2: Professional */}
                  <Grid item xs={12} md={3}>
                    <Card
                      className="flow-card"
                      sx={{
                        textAlign: "center",
                        p: 3,
                        height: "100%",
                        bgcolor: "#2e7d32",
                      }}
                    >
                      <Person sx={{ fontSize: 48, color: "white", mb: 2 }} />
                      <Typography
                        variant="h6"
                        sx={{ fontWeight: 600, mb: 1, color: "white" }}
                      >
                        2. Indépendant
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{ mb: 2, color: "white" }}
                      >
                        Réalise la mission
                      </Typography>
                      <Box
                        sx={{ bgcolor: "success.light", p: 2, borderRadius: 2 }}
                      >
                        <Typography
                          variant="body2"
                          sx={{ fontWeight: 600, color: "white" }}
                        >
                          Mission terminée
                        </Typography>
                        <CheckCircle
                          sx={{ fontSize: 20, mt: 1, color: "white" }}
                        />
                      </Box>
                    </Card>
                  </Grid>

                  {/* Arrow 2 */}
                  <Grid item xs={12} md={1} sx={{ textAlign: "center" }}>
                    <Box className="flow-arrow">
                      <TrendingUp
                        sx={{
                          fontSize: 32,
                          color: "primary.main",
                          transform: "rotate(90deg)",
                        }}
                      />
                    </Box>
                  </Grid>

                  {/* Step 3: Payment */}
                  <Grid item xs={12} md={3}>
                    <Card
                      className="flow-card"
                      sx={{
                        textAlign: "center",
                        p: 3,
                        height: "100%",
                        bgcolor: "#ed6c02",
                      }}
                    >
                      <Payment sx={{ fontSize: 48, color: "white", mb: 2 }} />
                      <Typography
                        variant="h6"
                        sx={{ fontWeight: 600, mb: 1, color: "white" }}
                      >
                        3. Paiement
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{ mb: 2, color: "white" }}
                      >
                        Rémunération finale
                      </Typography>
                      <Box
                        sx={{ bgcolor: "warning.light", p: 2, borderRadius: 2 }}
                      >
                        <Typography
                          variant="body2"
                          sx={{ fontWeight: 600, color: "white" }}
                        >
                          Taux horaire net
                        </Typography>
                        <Typography variant="caption" sx={{ color: "white" }}>
                          Après commission
                        </Typography>
                      </Box>
                    </Card>
                  </Grid>
                </Grid>
              </Box>
            </Paper>

            {/* Commission Details */}
            <Grid container spacing={4}>
              <Grid item xs={12} md={6}>
                <Card sx={{ p: 4, height: "100%", borderRadius: 3 }}>
                  <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
                    <AccountBalance
                      sx={{ fontSize: 32, color: "primary.main", mr: 2 }}
                    />
                    <Typography
                      variant="h5"
                      sx={{ fontWeight: 600, color: "white" }}
                    >
                      Commission Mission Sport
                    </Typography>
                  </Box>
                  <Box sx={{ textAlign: "center", mb: 3 }}>
                    <Typography
                      variant="h2"
                      sx={{
                        fontWeight: 700,
                        color: "primary.main",
                        mb: 1,
                      }}
                    >
                      20%
                    </Typography>
                    <Typography variant="body1" color="text.secondary">
                      Commission prélevée sur chaque mission
                    </Typography>
                  </Box>
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="body2" color="text.secondary">
                    Cette commission permet de maintenir et développer la
                    plateforme, d'assurer le support client et de garantir la
                    sécurité des transactions.
                  </Typography>
                </Card>
              </Grid>

              <Grid item xs={12} md={6}>
                <Card sx={{ p: 4, height: "100%", borderRadius: 3 }}>
                  <Typography
                    variant="h5"
                    sx={{ fontWeight: 600, mb: 3, color: "white" }}
                  >
                    Exemple de calcul
                  </Typography>
                  <Box sx={{ mb: 3 }}>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        mb: 1,
                      }}
                    >
                      <Typography variant="body2">
                        Taux horaire brut :
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        50€/h
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        mb: 1,
                      }}
                    >
                      <Typography variant="body2">
                        Frais de service :
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        5€
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        mb: 1,
                      }}
                    >
                      <Typography variant="body2">Total facturé :</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        55€
                      </Typography>
                    </Box>
                    <Divider sx={{ my: 2 }} />
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        mb: 1,
                      }}
                    >
                      <Typography variant="body2" sx={{ color: "orange" }}>
                        Commission (20%) :
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{ fontWeight: 600, color: "orange" }}
                      >
                        -10€
                      </Typography>
                    </Box>
                    <Box
                      sx={{ display: "flex", justifyContent: "space-between" }}
                    >
                      <Typography
                        variant="body1"
                        sx={{ fontWeight: 600, color: "white" }}
                      >
                        Rémunération nette :
                      </Typography>
                      <Typography
                        variant="body1"
                        sx={{ fontWeight: 700, color: "success.main" }}
                      >
                        40€
                      </Typography>
                    </Box>
                  </Box>
                </Card>
              </Grid>
            </Grid>
          </div>
        </section>

        {/* CTA Section */}
        <section className="cta-section">
          <div className="container">
            <div className="cta-content">
              <h2>Prêt à trouver votre prochain talent ?</h2>
              <p style={{ color: 'black' }}>
                Rejoignez des centaines d'organisations qui font confiance à
                Mission Sport pour leurs recrutements.
              </p>
              <div className="cta-actions">
                <Link to="/register" className="btn btn-primary btn-large">
                  Créer mon compte organisation
                </Link>
                <Link to="/contact" className="btn btn-secondary btn-large">
                  Parler à un conseiller
                </Link>
              </div>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default OrganisationPresentation;
